import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import AuthProvider from './auth/AuthProvider';
import App from './App';
import './styles/theme.css';
// One-off dev utility: exposes window.importLiaisons() on the localhost dev
// server only (guarded by import.meta.env.DEV inside the module). Safe to
// remove once the liaison import is done.
import './dev/importLiaisons';

/**
 * When popup-based auth runs (e.g. the on-demand Mail.Send consent in
 * services/email.js), MSAL opens a popup window that Microsoft redirects back
 * to this same address. The portal must NOT boot inside that popup: booting
 * consumes the sign-in response (so the main window waits forever) and
 * renders a second copy of the app inside the popup. MSAL names its popups
 * "msal....", so when this window is a popup with that name we render
 * nothing — the main window reads the result from the popup's URL and closes
 * it automatically.
 *
 * Safari can clear window.name during the cross-origin hops through
 * login.microsoftonline.com / sso.godaddy.com, so we also treat "has an
 * opener + a blank name + an auth response in the URL" as an MSAL popup.
 * (A normal tab returning from the redirect sign-in has no opener, so the
 * main window is never affected.)
 */
const hasAuthResponseInUrl = /[#&?](code|error)=/.test(
  `${window.location.search}${window.location.hash}`,
);
const isMsalPopup =
  window.opener &&
  window.opener !== window &&
  (window.name?.startsWith('msal.') ||
    (window.name === '' && hasAuthResponseInUrl));

if (!isMsalPopup) {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <AuthProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AuthProvider>
    </React.StrictMode>,
  );
}
